import { InputProps, OutlinedInput } from '@mui/material';
import { defaultMuiComponents, MaterialActionProps } from '@react-querybuilder/material';
import { ValueSource } from 'react-querybuilder';
import { CONDITION_CONTROLS, withConditionValueCell } from 'src/components/ConditionsBuilder';
import {
  ActionType,
  DataConditionOperator,
  RulesArray,
  SendConversationMessageSource,
} from 'src/graphql';
import {
  ConditionValueEditor,
  CustomValueEditor,
  CustomValueSelector,
  RQB_NEW_FIELD_NAME,
} from './rules-form-ui-controls';

export const muiComponents = {
  ...defaultMuiComponents,
  Input: (props: InputProps) => <OutlinedInput size="small" {...props} />,
} as MaterialActionProps['muiComponents'];

/** The builder's controls with the rules engine's value editor, for screens with their own layout. */
export const conditionsCustomControls = {
  ...CONDITION_CONTROLS,
  valueEditor: withConditionValueCell(ConditionValueEditor),
};

export const actionsCustomControls = {
  addRuleAction: () => null,
  addGroupAction: () => null,
  operatorSelector: () => null,
  valueSelector: CustomValueSelector,
  valueEditor: CustomValueEditor,
  removeRuleAction: () => null,
};

export const DEFAULT_CONDITION = {
  rules: [],
  combinator: 'and',
  not: false,
};
export const PLACEHOLDER_ACTION_RULE = {
  id: '',
  field: RQB_NEW_FIELD_NAME,
  operator: '=' as DataConditionOperator,
  valueSource: 'value' as ValueSource,
  value: '',
};

export const DEFAULT_DATA_ACTION = {
  id: '',
  rules: [] as RulesArray,
  combinator: 'and',
  not: false,
};

export const BOOLEAN_CONTROL_OPTIONS = [
  { label: 'True', id: 'true', value: true },
  { label: 'False', id: 'false', value: false },
];

export function getDefaultActionFormState(actionType: ActionType) {
  /**
   * This method returns the default state for an empty form when
   * a new action type is selected.
   * The fields which render Autocomplete control should have `null` value initially,
   * so those `RulesFormActionRule` fields' typings were extended with ` | null` to represent this.
   * Without a value specified, Autocomplete will be properly rendered as an empty input
   * with a placeholder.
   */
  switch (actionType) {
    case ActionType.SendSlackMessage:
      return {
        id: crypto.randomUUID(),
        type: ActionType.SendSlackMessage,
        workspaceType: null,
        channel: '',
        message: '',
      };
    case ActionType.CreateTwilioTask:
      return {
        id: crypto.randomUUID(),
        type: ActionType.CreateTwilioTask,
        taskChannel: '',
      };
    case ActionType.RunTriage:
      return {
        id: crypto.randomUUID(),
        type: ActionType.RunTriage,
        conversationTriageId: null,
        userInstructions: '',
      };
    case ActionType.DeleteConversation:
      return {
        id: crypto.randomUUID(),
        type: ActionType.DeleteConversation,
      };
    case ActionType.BlockConversationRequester:
      return {
        id: crypto.randomUUID(),
        type: ActionType.BlockConversationRequester,
      };
    case ActionType.EndSmsSession:
      return {
        id: crypto.randomUUID(),
        type: ActionType.EndSmsSession,
      };
    case ActionType.SendConversationMessage:
      return {
        id: crypto.randomUUID(),
        type: ActionType.SendConversationMessage,
        isPrivate: null,
        messageSource: SendConversationMessageSource.StaticText,
        message: '',
        aiPrompt: '',
        target: null,
      };
    case ActionType.CreateConversationMessageDraft:
      return {
        id: crypto.randomUUID(),
        type: ActionType.CreateConversationMessageDraft,
        isPrivate: null,
        messageSource: SendConversationMessageSource.StaticText,
        message: '',
        aiPrompt: '',
      };
    case ActionType.ManageConversationLabels:
      return {
        id: crypto.randomUUID(),
        type: ActionType.ManageConversationLabels,
        labelIds: [],
        operation: null,
      };
    case ActionType.ManageSubAssignees:
      return {
        id: crypto.randomUUID(),
        type: ActionType.ManageSubAssignees,
        platformUserIds: [],
        operation: null,
      };
    case ActionType.Webhook:
      return {
        id: crypto.randomUUID(),
        type: ActionType.Webhook,
        url: '',
        method: null,
        body: '',
        headers: '',
      };
    case ActionType.Signal:
      return {
        id: crypto.randomUUID(),
        type: ActionType.Signal,
        operation: null,
        notificationsTargetType: null,
      };
    case ActionType.Data:
      return {
        ...DEFAULT_DATA_ACTION,
        id: crypto.randomUUID(),
        rules: [
          {
            ...PLACEHOLDER_ACTION_RULE,
            id: crypto.randomUUID(),
          },
        ],
      };
  }
}
